import React, { useState, useEffect } from 'react';
import { useRMSelector } from 'redux/hooks';
import {
  selectScoreGroup,
  selectAutoSelectNextWell,
} from 'redux/selectors/StateSelectors';
import TreeCommandCard from 'components/card/TreeCommandCard';
import KeyStroke from '../KeyStroke';
import { isNullOrUndefined, GetScoreDefn } from 'utils/Global';
import EScoreType from 'enums/EScoreType';
import './DefaultScores.scss';
import { DropDetails } from 'components/well/constants';
import { ScoreDefinitionDTO } from 'business/models/Models';
import { useManualScoreGroups } from 'redux/hooks/common';
import {
  ScoresOfWellDrops,
  WellNavigationDetails,
} from 'components/Experiment/constants';

interface Props {
  selectedWellsScores: ScoresOfWellDrops[][];
  selectedWellDetails: Array<WellNavigationDetails | DropDetails>;
  applyScoring: (wellDropScoreId, wellDropScoreGroupId) => void;
  imageBatchId?: number;
  dropNumber?: number;
  cpId?: number;
}
function DefaultScores(props: Props) {
  const {
    selectedWellsScores,
    selectedWellDetails,
    applyScoring,
    imageBatchId,
    dropNumber,
    cpId,
  } = props;
  const wdScores = GetScoreDefn();
  const [ScoreDefinitions, setScoreDefinitions] = useState(wdScores);
  const scoreGroup = useRMSelector((state) => selectScoreGroup(state));
  const autoSelectNextWell = useRMSelector(selectAutoSelectNextWell);
  const [nodeMenuDetails, setNodeManuDetails] = useState([]);
  const [WellDropScoresView, setWellDropScoresView] = useState([]);
  const scoresOptionsList = useManualScoreGroups();
  const firstWell = selectedWellDetails?.[0];

  const _imageBatchId =
    firstWell && 'imageBatchId' in firstWell ? firstWell.imageBatchId : imageBatchId;

  const _dropNumber =
    firstWell && 'dropNumber' in firstWell ? firstWell.dropNumber : dropNumber;

  const _cpID = firstWell && 'cpID' in firstWell ? firstWell.cpID : cpId;

  const customBoxStyle = {
    height: '10vh',
    width: '5vw',
    minHeight: '70px',
    minWidth: '70px',
  };

  const customTextStyle = {
    fontSize: '0.75rem',
    lineHeight: '0.875rem',
    textAlign: 'center',
    padding: '0.75rem',
    letterSpacing: 'normal',
  };

  function scoreWellDrop(wellDropScoreId, wellDropScoreGroupId) {
    if (!_imageBatchId) {
      return;
    }
    applyScoring(wellDropScoreId, wellDropScoreGroupId);
  }

  useEffect(() => {
    if (!isNullOrUndefined(wdScores)) {
      setScoreDefinitions(wdScores);
    }
  }, [wdScores]);

  const isScoreActive = (wellDropScoreId: number, wellDropScoreGroupId: number) => {
    if (!selectedWellsScores) {
      return false;
    }
    return selectedWellsScores
      .flatMap((scores) =>
        scores.filter((s) =>
          selectedWellDetails.some(
            (well) =>
              s.scoreBatchId === _imageBatchId &&
              _dropNumber === s.dropNumber &&
              well.wellNum === s.wellNumber,
          ),
        ),
      )
      .some(
        (score) =>
          score.wellDropScoreGroupId === wellDropScoreGroupId &&
          score.scoreType !== EScoreType.Inherited &&
          score.wellDropScoreId === wellDropScoreId,
      );
  };
  useEffect(() => {
    if (isNullOrUndefined(ScoreDefinitions)) {
      return;
    }
    const ibWellScores = ScoreDefinitions.filter(
      (scores) => scores.isAutoScore === false,
    );
    setWellDropScoresView(ibWellScores);
  }, [scoreGroup, ScoreDefinitions]);

  useEffect(() => {
    setNodeManuDetails(
      WellDropScoresView.map((scores: ScoreDefinitionDTO) => {
        const isActiveScore = isScoreActive(
          scores.wellDropScoreId,
          scores.wellDropScoreGroupId,
        );
        return {
          active: isActiveScore,
          color: scores.color,
          hotKey: scores.hotKey,
          onClick: () =>
            scoreWellDrop(scores.wellDropScoreId, scores.wellDropScoreGroupId),
          title: scores.scoreName,
          scoreGroupName: scores.scoreGroupName,
        };
      }),
    );
  }, [
    WellDropScoresView,
    selectedWellsScores,
    selectedWellDetails,
    autoSelectNextWell,
    _dropNumber,
    imageBatchId,
    _cpID,
  ]);

  function compare(a, b) {
    // Use toUpperCase() to ignore character casing
    const scoreA = a.scoreGroupsId;
    const scoreB = b.scoreGroupsId;

    let comparison = 0;
    if (scoreA > scoreB) {
      comparison = 1;
    } else if (scoreA < scoreB) {
      comparison = -1;
    }
    return comparison;
  }

  function compareHotKey(a, b) {
    // Use toUpperCase() to ignore character casing
    const scoreA = a.hotKey;
    const scoreB = b.hotKey;

    let comparison = 0;
    if (scoreA > scoreB) {
      comparison = 1;
    } else if (scoreA < scoreB) {
      comparison = -1;
    }
    return comparison;
  }

  function createManualScoresNodes() {
    const nodeComponents = [];
    let nodeComponentGroups = [];
    const tempManualScoresGroups: any[] = [
      ...scoresOptionsList?.filter((scores) => scores.isAutoScore === false),
    ];
    tempManualScoresGroups.sort(compare);
    tempManualScoresGroups.forEach((scoresGroup, index) => {
      const groupScoreList = nodeMenuDetails.filter(
        (scores) => scores.scoreGroupName === scoresGroup.name,
      );
      if (scoresGroup.name !== 'Interesting') {
        if (index >= 0) {
          nodeComponents.push(
            <div className="bg-stone-200 font-bold px-1">{scoresGroup.name}</div>,
          );
        }
        nodeComponentGroups = [];
        groupScoreList.sort(compareHotKey);
        groupScoreList.forEach((nodeMenuDetail) => {
          nodeComponentGroups.push(
            <TreeCommandCard
              onClick={nodeMenuDetail.onClick}
              iconName={nodeMenuDetail.iconName}
              title={nodeMenuDetail.title}
              active={nodeMenuDetail.active}
              customStyle={customBoxStyle}
              customTextStyle={customTextStyle}
            >
              <KeyStroke keystroke={nodeMenuDetail.hotKey} color={nodeMenuDetail.color} />
            </TreeCommandCard>,
          );
        });
        nodeComponents.push(<ul className="scores-list">{nodeComponentGroups}</ul>);
      }
    });
    return nodeComponents;
  }
  if (!firstWell || !scoresOptionsList) {
    return;
  }
  return (
    <div id='commandpanel-exp-defaultscore-wrapper'
      style={{
        width: '100%',
        height: '100%',
      }}
    >
      <div id="commandpanel-exp-defaultscore-experimentpanel" style={{ height: '90%', overflow: 'auto' }}>
        <div id="commandpanel-exp-defaultscore-manualscores-wrapper" className="experimentPanel" style={{ height: '75vh', overflow: 'auto' }}>
          {createManualScoresNodes()}
        </div>
      </div>
    </div>
  );
}

export default React.memo(DefaultScores);
